import { Location } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    effect,
    inject,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { PermissionListBloc } from '@connect-services/client/blocs/permission-list.bloc';
import { RoleBloc } from '@connect-services/client/blocs/role.bloc';
import { RoleListBloc } from '@connect-services/client/blocs/role-list.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { RouteCompletePathEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import { RolePermissionListComponent } from '@connect-services/client/pages/management/organization/role-detail/role-permission-list/role-permission-list.component';
import { DetailPanelService, PanelAction } from '@connect-services/client/services/detail-panel.service';
import { HasPermissionsService } from '@connect-services/client/services/has-permissions.service';
import { OrganizationAccessPermissionsService } from '@connect-services/client/services/organization-access-permissions.service';
import { PermissionsFormService } from '@connect-services/client/services/permissions-form.service';
import { ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { Permission, PermissionAliasEnum, Role, RolesService } from '@connect-services/src/sdk/api';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RolePermissionListComponent, TranslocoPipe],
    selector: 'app-role-detail',
    templateUrl: './role-detail.component.html',
})
export class RoleDetailComponent {
    public readonly accessPermissionsLabel: WritableSignal<string[]> = signal([]);
    public readonly managePermissionsLabel: WritableSignal<string[]> = signal([]);
    public readonly permissionsFormService = inject(PermissionsFormService);
    public readonly role: WritableSignal<Role | null> = signal(null);

    private readonly destroyRef = inject(DestroyRef);
    private readonly detailPanelService = inject(DetailPanelService);
    private readonly hasPermissionsService = inject(HasPermissionsService);
    private readonly matDialog = inject(MatDialog);
    private readonly organizationAccessPermissionsService = inject(OrganizationAccessPermissionsService);
    private readonly permissionListBloc = inject(PermissionListBloc);
    private readonly roleBloc = inject(RoleBloc);
    private readonly roleListBloc = inject(RoleListBloc);
    private readonly rolesService = inject(RolesService);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    public constructor(private location: Location) {
        this.destroyRef.onDestroy(() => {
            this.detailPanelService.clear();
        });

        effect(() => {
            if (!this.roleBloc.value()) {
                return;
            }

            untracked(() => this.initDetailPanel());

            this.initRolePermission();
        });
    }

    public async openRoleEdition(): Promise<void> {
        if (!this.hasPermissionsService.checkPermissions([PermissionAliasEnum.MANAGE_ROLE])) {
            return;
        }

        const panelParam = this.role()!.id;

        await this.router.navigate([
            ...RouteCompletePathEnum.USERS_ROLES,
            { outlets: { detailPanel: ['role', panelParam, RoutePathEnum.ROLE_EDITION] } },
        ]);
    }

    private confirmDelete(): void {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('delete'),
                description: this.translocoService.translate('irreversibleAction'),
                title: this.translocoService.translate('doYouWantToDelete', {
                    entity: 'role',
                }),
                type: ModalType.DANGER,
            },
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }

            await this.deleteRole();
            this.location.back();
        });
    }

    private async deleteRole(): Promise<void> {
        await firstValueFrom(this.rolesService.roleControllerDelete({ id: this.role()!.id }));

        await this.roleListBloc.load({}, { refresh: true, useLastLoadParameters: true });
        this.snackBarService.openSuccessSnackBar('roleDeletedSuccess');
    }

    private getPanelActions(role: Role): PanelAction[] {
        const canEditRole = this.organizationAccessPermissionsService.canManageRole();
        const state = this.organizationAccessPermissionsService.getDeleteRoleState(role);

        return [
            new PanelAction({
                defaultDisabledTooltipMessage: this.translocoService.translate('notPossibleDelete'),
                icon: ['fas', 'trash'],
                isActive: state.allowed,
                label: this.translocoService.translate('delete'),
                onClick: this.confirmDelete.bind(this),
                type: ButtonType.ALERT_SECONDARY,
                ...(!state.allowed && {
                    disabledReason: [
                        this.translocoService.translate(state.disabledReasonKey ?? 'insufficientPermissions'),
                    ],
                }),
            }),
            new PanelAction({
                defaultDisabledTooltipMessage: this.translocoService.translate('notPossibleEdit'),
                icon: ['fas', 'pen'],
                isActive: canEditRole,
                label: this.translocoService.translate('edit'),
                onClick: this.openRoleEdition.bind(this),
                ...(!canEditRole && { disabledReason: [this.translocoService.translate('insufficientPermissions')] }),
            }),
        ];
    }

    private initDetailPanel(): void {
        const role = this.roleBloc.value();
        if (!role) {
            return;
        }

        this.role.set(role);

        this.detailPanelService.title.set(role.name);
        this.detailPanelService.actions.set(this.getPanelActions(role));
        this.detailPanelService.navigateTo.set(RouteCompletePathEnum.USERS_ROLES);
    }

    private initRolePermission(): void {
        const permissions = this.permissionListBloc.value()?.items ?? [];
        this.accessPermissionsLabel.set([]);
        this.managePermissionsLabel.set([]);

        const accessPermissions: string[] = [];
        const managePermissions: string[] = [];

        this.splitPermission(permissions, accessPermissions, managePermissions);

        this.accessPermissionsLabel.set(accessPermissions);
        this.managePermissionsLabel.set(managePermissions);
    }

    private splitPermission(permissionList: Permission[], accessList: string[], manageList: string[]): void {
        permissionList
            .toSorted((permissionA, permissionB) => {
                const permissionAliasA = this.permissionsFormService.convertAlias(permissionA.alias);

                const permissionAliasB = this.permissionsFormService.convertAlias(permissionB.alias);

                return this.translocoService
                    .translate(permissionAliasA)
                    .localeCompare(this.translocoService.translate(permissionAliasB));
            })
            .forEach((permission) => {
                if (permission.alias.includes('read')) {
                    accessList.push(this.permissionsFormService.convertAlias(permission.alias));
                }
                if (permission.alias.includes('manage')) {
                    manageList.push(this.permissionsFormService.convertAlias(permission.alias));
                }
            });
    }
}
